import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { QuillModule } from 'ngx-quill';
import { concatMap, finalize, from, switchMap, tap, toArray } from 'rxjs';

import { InspectableSession, InspectionAttachment } from '../../../core/interfaces/inspection';
import { InspectionService } from '../../../core/services/inspection.service';
import { SubjectChoiceService } from '../../../core/services/utils/subject-choice.service';
import { FileSizePipe } from '../../../shared/pipes/file-size.pipe';

/**
 * Rédiger — ou reprendre — mon rapport d'inspection sur une séance, avec ses
 * pièces jointes.
 *
 * <p>`<dialog>` natif : peint dans la couche supérieure, hors de tout contexte
 * d'empilement de la coquille. Il s'ouvre quand l'hôte lui donne une séance,
 * et le lui rend fermé. Marquer inspecté et rédiger sont le même geste : un
 * rapport vide est refusé. Les pièces partent après le rapport, une à une ;
 * chaque pièce acceptée est acquise, même si la suivante échoue.</p>
 */
@Component({
  selector: 'app-inspection-report-dialog',
  standalone: true,
  imports: [DatePipe, FormsModule, TranslateModule, QuillModule, FileSizePipe],
  templateUrl: './inspection-report-dialog.component.html',
  styleUrl: './inspection-report-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InspectionReportDialogComponent implements AfterViewInit {
  private readonly service = inject(InspectionService);
  private readonly subjectChoice = inject(SubjectChoiceService);

  @ViewChild('dialog') private dialog?: ElementRef<HTMLDialogElement>;

  /** La séance dont on rédige le rapport ; `null` ferme. */
  @Input() set session(value: InspectableSession | null | undefined) {
    this.target.set(value ?? null);
    if (value) this.prepare(value);
    this.syncOpen();
  }

  /** Le rapport — et ses pièces — sont en base : l'hôte relit sa liste. */
  @Output() readonly saved = new EventEmitter<void>();
  /** Quelque chose a changé en base sans que le dialogue se ferme (pièce retirée, pièce refusée après un rapport accepté). */
  @Output() readonly changed = new EventEmitter<void>();
  @Output() readonly closed = new EventEmitter<void>();

  readonly target = signal<InspectableSession | null>(null);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  report = '';

  // ── Pièces jointes ─────────────────────────────────────────
  /** Ce que mon rapport porte déjà. */
  readonly attachments = signal<InspectionAttachment[]>([]);
  /** Ce qui partira avec le prochain enregistrement. */
  readonly pending = signal<File[]>([]);

  /** Mêmes bornes que le serveur (`UploadRules.InspectionAttachment`). */
  readonly attachAccept = '.pdf,.doc,.docx,.xls,.xlsx';
  private static readonly AttachMaxBytes = 20 * 1024 * 1024;
  private static readonly AttachMax = 10;

  /**
   * Barre d'outils volontairement réduite : un rapport se lit, il ne se met
   * pas en page. Gras, italique, listes et titres suffisent à structurer un constat.
   */
  readonly editorModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ header: [2, 3, false] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['clean'],
    ],
  };

  private wantOpen = false;

  ngAfterViewInit(): void {
    this.syncOpen();
  }

  /** Marquer inspecté et rédiger sont le même geste : un rapport vide est refusé. */
  get reportValid(): boolean {
    return plainText(this.report).length > 0;
  }

  subjectsOf(s: InspectableSession): string {
    const list = s.subjects ?? [];
    if (list.length === 0) return s.subjectName ?? '';
    return list.map((x) => this.subjectChoice.label(x, list)).join(' · ');
  }

  close(): void {
    if (this.saving()) return;
    this.wantOpen = false;
    this.dialog?.nativeElement.close();
  }

  /** Fermeture faite par le navigateur (Échap, close()) : l'hôte en est informé. */
  onClosed(): void {
    this.wantOpen = false;
    this.target.set(null);
    this.closed.emit();
  }

  submit(): void {
    const s = this.target();
    if (!s || this.saving()) return;

    if (!this.reportValid) {
      this.error.set('INSPECTION.REPORT_REQUIRED');
      return;
    }

    this.saving.set(true);
    this.error.set(null);

    // Ce qui a passé est en base : si une pièce échoue ensuite, la liste
    // derrière le dialogue doit déjà le savoir — sinon, à la fermeture, la
    // séance resterait « non inspectée » et sa ligne porterait l'ancien rapport.
    let reportSaved = false;

    this.service
      .saveReport(s.id, this.report)
      .pipe(
        tap(() => { reportSaved = true; }),
        // Les pièces suivent le rapport, une à une : le serveur les refuse
        // sans rapport, et deux dépôts simultanés se disputeraient le plafond.
        switchMap(() => from(this.pending()).pipe(
          concatMap((file) => this.service.addAttachment(s.id, file).pipe(
            tap((res) => {
              if (res?.data) this.attachments.update((list) => [...list, res.data]);
              this.pending.update((list) => list.filter((f) => f !== file));
            }),
          )),
          toArray(),
        )),
        finalize(() => this.saving.set(false)),
      )
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.close();
          this.saved.emit();
        },
        error: (err) => {
          this.error.set(messageFrom(err));
          if (reportSaved) this.changed.emit();
        },
      });
  }

  /**
   * Fichiers choisis : vérifiés ici comme ils le seront au serveur, pour que
   * le refus arrive avant l'envoi et non après.
   */
  pickFiles(event: Event): void {
    const input = event.target as HTMLInputElement;
    const chosen = Array.from(input.files ?? []);
    input.value = '';
    if (!chosen.length) return;

    const accepted = this.attachAccept.split(',');
    const extensionOf = (name: string) => name.slice(name.lastIndexOf('.')).toLowerCase();
    if (chosen.some((f) => !accepted.includes(extensionOf(f.name)) || f.size > InspectionReportDialogComponent.AttachMaxBytes)) {
      this.error.set('INSPECTION.ATTACH.INVALID');
      return;
    }
    if (this.attachments().length + this.pending().length + chosen.length > InspectionReportDialogComponent.AttachMax) {
      this.error.set('INSPECTION.ATTACH.LIMIT');
      return;
    }

    this.error.set(null);
    this.pending.update((list) => [...list, ...chosen]);
  }

  removePending(file: File): void {
    this.pending.update((list) => list.filter((f) => f !== file));
  }

  removeAttachment(attachment: InspectionAttachment): void {
    const s = this.target();
    if (!s || this.saving()) return;
    this.service.removeAttachment(s.id, attachment.id).subscribe({
      next: () => {
        this.attachments.update((list) => list.filter((a) => a.id !== attachment.id));
        // La ligne derrière le dialogue porte encore la pièce : l'hôte la relit,
        // sinon Annuler puis rouvrir la ferait réapparaître avec un lien mort.
        this.changed.emit();
      },
      error: (err) => this.error.set(messageFrom(err)),
    });
  }

  /** L'icône du type de document, d'après l'extension. */
  iconOf(fileName: string): string {
    const ext = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase();
    if (ext === 'pdf') return 'fa-file-pdf';
    if (ext === 'doc' || ext === 'docx') return 'fa-file-word';
    if (ext === 'xls' || ext === 'xlsx') return 'fa-file-excel';
    return 'fa-file';
  }

  private prepare(s: InspectableSession): void {
    this.error.set(null);
    // Le rapport déjà déposé par cet inspecteur, s'il existe : on modifie, on
    // n'empile pas.
    this.report = s.inspections[0]?.report ?? '';
    this.attachments.set(s.inspections[0]?.attachments ?? []);
    this.pending.set([]);
    this.wantOpen = true;
  }

  /** L'état du `<dialog>` suit l'entrée — y compris quand elle arrive avant la vue. */
  private syncOpen(): void {
    const el = this.dialog?.nativeElement;
    if (!el) return;
    if (this.wantOpen && !el.open) el.showModal();
    if (!this.wantOpen && el.open) el.close();
  }
}

/**
 * Texte nu d'un contenu de l'éditeur. Quill rend `<p><br></p>` pour un champ
 * vidé : le tester tel quel laisserait passer un rapport vide.
 */
function plainText(html: string): string {
  return (html ?? '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
}

/** Le message du serveur, lisible par l'utilisateur ; sinon le libellé — jamais un texte technique. */
function messageFrom(err: unknown): string {
  const e = err as { error?: { message?: string } };
  return e?.error?.message || 'INSPECTION.SAVE_ERROR';
}
